/**
 * Trame pédagogique Passerelle A2 → A (7 h) — CODE 2 ROUES.
 * Grille pédagogique enrichie 12 champs par compétence.
 * Contenu 100% neutre : aucune mention d'école ou d'entreprise.
 */
import { CINQ_QUESTIONS, METHODE_BASELINE } from './pedagogieCommune';

export const INTRO_PASSERELLE = {
  baseline: METHODE_BASELINE,
  questions: CINQ_QUESTIONS,
  principe: "La passerelle A2 → A ne consiste pas à reprendre intégralement la formation A2. Elle s'appuie sur l'expérience acquise pendant la période A2 : les exercices ne sont pas réalisés de manière systématique, ils sont sélectionnés par le formateur en fonction du niveau, de l'expérience et des besoins réels du conducteur.",
};

/* ================================================================== */
/*  PHASE 1 — THÉORIE (~2 h)                                           */
/* ================================================================== */
const P1_THEORIE = [
  {
    id: 'admin-equip',
    title: 'Rappels administratifs & équipements',
    objectif: "Le conducteur connaît les conditions légales du passage en A et adapte son équipement à une moto plus performante.",
    pourquoi: 'Cadrer les conditions légales du passage A2 → A et rappeler l\u2019importance des équipements sur une moto plus performante.',
    comment: ['Conditions du passage A2 → A (2 ans + 7 h)', 'Équipements obligatoires', 'Équipements complémentaires (dorsale, blouson homologué)', 'Vérifications spécifiques moto plus puissante'],
    exercices: [
      { nom: 'Comparatif équipement A2 → A', niveau: 1, description: 'Ce que l\u2019on gardait, ce que l\u2019on ajoute (dorsale, casque intégral).' },
      { nom: 'Vérifications sur moto plus puissante', niveau: 2, description: 'Focus pneus, chaîne, freinage.' },
    ],
    observation: ['Sous-équipement pour «une simple leçon»', 'Vérifications superficielles'],
    risques: ['Sous-équipement face à des vitesses plus élevées', 'Défaut mécanique aggravé par la puissance'],
    influences: ['Type de moto', 'Trajet', 'Météo'],
    erreurs: ['«J\u2019ai roulé 2 ans en A2, je connais»', 'Négliger la spécificité d\u2019une moto plus lourde'],
    remediation: ['Reprendre point par point', 'Analyse comparative moto A2 / moto A'],
    questions: ["«En quoi ton équipement doit-il évoluer pour rouler en catégorie A ?»"],
    critere: 'Le conducteur cite ses conditions légales de passage et adapte son équipement à la moto utilisée.',
  },
  {
    id: 'retour-experience',
    title: 'Retour d\u2019expérience de la période A2',
    objectif: "Le formateur part du vécu du stagiaire pour identifier ses points forts et les zones à travailler.",
    pourquoi: 'Le vécu concret du conducteur A2 permet un travail bien plus ciblé que la théorie abstraite.',
    comment: ['Qu\u2019est-ce qui a changé depuis le permis A2 ?', 'Quelles situations posent encore problème ?', 'Quels trajets ont été réalisés ?', 'Quels objectifs avec le passage en A ?'],
    exercices: [
      { nom: 'Entretien guidé', niveau: 1, description: '4 questions ouvertes structurées.' },
      { nom: 'Auto-évaluation par domaines', niveau: 2, description: 'L\u2019élève note lui-même ses points forts / faibles par thème.' },
    ],
    observation: ['Réponses générales sans exemple', 'Difficulté à nommer un point faible'],
    risques: ['Sous-estimer les progrès à faire', 'Négliger un point identifié comme fragile'],
    influences: ['Fréquence de conduite', 'Type de moto A2', 'Environnement (urbain / route)'],
    erreurs: ['«Tout va bien»', 'Se dévaloriser à l\u2019excès'],
    remediation: ['Reformuler les questions avec exemples concrets', 'Aider à hiérarchiser les priorités'],
    questions: ["«Quelles situations te mettent encore en difficulté après ta période A2 ?»", "«Un souvenir marquant de peur ou d\u2019inconfort à moto ?»"],
    critere: 'Le stagiaire identifie 2 points forts et 2 axes de progrès concrets, avec des exemples issus de son vécu.',
  },
  {
    id: 'projection-A',
    title: 'Projection vers la conduite d\u2019une moto catégorie A',
    objectif: "Le conducteur anticipe les différences réelles avec une moto plus puissante avant de la piloter.",
    pourquoi: 'Anticiper évite l\u2019effet de surprise et la sur-confiance.',
    comment: ['Puissance disponible plus importante', 'Reprises plus vives à mi-régime', 'Freinage plus performant mais plus exigeant', 'Comportement plus dynamique en virage', 'Distances de freinage à réévaluer'],
    exercices: [
      { nom: 'Comparaison caractéristiques moto A2 vs A', niveau: 2, description: 'Puissance, poids, couple, hauteur de selle.' },
    ],
    observation: ['Sur-confiance verbale', 'Sous-estimation des différences'],
    risques: ['Sur-régime involontaire', 'Ouverture des gaz trop rapide', 'Sur-estimation de sa marge'],
    influences: ['Cylindrée', 'Rapport poids / puissance', 'Type de moto (roadster, sportive, trail)'],
    erreurs: ['«C\u2019est juste une A2 plus puissante»', 'Vouloir tester la puissance en début de séance'],
    remediation: ['Reprise par vidéo de comportement moto puissante', 'Insister sur les 15 premières minutes de prise en main'],
    questions: ["«Sur quoi vas-tu particulièrement faire attention lors de tes 5 premières minutes sur la moto A ?»"],
    critere: 'Le conducteur cite 4 différences comportementales attendues et propose une stratégie de prise en main progressive.',
    aRetenir: 'Une moto plus puissante ne demande pas de conduire plus vite, mais de conduire plus juste.',
  },
  {
    id: 'accidentologie',
    title: 'Accidentologie & accidents-types à moto',
    objectif: "Le conducteur identifie les situations accidentogènes spécifiques au motard expérimenté sur moto plus performante.",
    pourquoi: 'Le passage en A introduit un pic d\u2019accidentologie lié au décalage entre confiance et performance de la moto.',
    comment: ['Intersections', 'Virages (vitesse mal évaluée)', 'Dépassements', 'Freinage tardif', 'Ouverture de portière', 'Sortie de piste', 'Chute à faible vitesse'],
    exercices: [
      { nom: 'Étude de 3 cas concrets', niveau: 2, description: 'Focus sur des accidents en début de période A.' },
    ],
    observation: ['Difficulté à hiérarchiser les causes', 'Focus systématique sur l\u2019autre usager'],
    risques: ['Excès de confiance', 'Sous-évaluation d\u2019un virage', 'Anticipation insuffisante'],
    influences: ['Ancienneté', 'Fatigue', 'Habitudes acquises', 'Type de moto'],
    erreurs: ['«L\u2019accident, ça n\u2019arrive qu\u2019aux autres»', 'Négliger la fatigue en fin de journée'],
    remediation: ['Reprise avec statistiques ONISR par cylindrée', 'Analyse d\u2019un cas par catégorie de risque'],
    questions: ["«À quel moment de la période A un motard est-il le plus exposé ?»"],
    critere: 'Le conducteur cite 4 situations à risque spécifiques et 2 comportements préventifs pour chacune.',
  },
];

/* ================================================================== */
/*  PHASE 2 — HORS CIRCULATION (~2 h)                                  */
/* ================================================================== */
const P2_HORS_CIRC = [
  {
    id: 'allure-lente',
    title: 'Allure très lente & braquage',
    objectif: "Le motard vérifie son contrôle basse vitesse sur une moto plus lourde et plus puissante.",
    pourquoi: 'Point de départ obligatoire : la nouvelle moto doit d\u2019abord être maîtrisée à basse vitesse avant tout travail dynamique.',
    comment: ['Regard loin', 'Zone de patinage', 'Frein arrière dosé', 'Braquage complet'],
    exercices: [
      { nom: 'Passage lent en ligne droite', niveau: 2, description: 'Sentir la moto à basse vitesse.' },
      { nom: 'Huit à faible allure', niveau: 3, description: 'Vérifier la coordination sur les 2 côtés.' },
    ],
    observation: ['Calée', 'Chute intérieure', 'Guidonnage', 'Bras crispés'],
    risques: ['Chute à faible vitesse', 'Chute lors du braquage complet'],
    influences: ['Poids de la moto', 'Adhérence', 'Charge', 'Braquage disponible'],
    erreurs: ['Vouloir «attaquer» tout de suite', 'Négliger le frein arrière sur moto plus lourde'],
    remediation: ['Reprise patinage moto immobile', 'Refaire huit très large avant de resserrer'],
    questions: ["«En quoi cette moto est-elle différente à basse vitesse par rapport à ton A2 ?»"],
    critere: 'Le motard réalise 3 huit consécutifs sans poser le pied, avec regard porté à la sortie.',
  },
  {
    id: 'gymkhana',
    title: 'Gymkhana',
    objectif: "Le motard enchaîne des changements de direction rapprochés en travaillant regard, coordination et gestion de l'allure.",
    pourquoi: 'Rassemble regard + coordination + patinage + contre-braquage : synthèse basse vitesse dynamique.',
    comment: ['Regard anticipé', 'Trajectoires fluides', 'Coordination embrayage / frein / gaz', 'Position stable'],
    exercices: [
      { nom: 'Gymkhana simple', niveau: 3, description: 'Parcours en boucles.' },
      { nom: 'Gymkhana avec entrée/sortie chronométrée', niveau: 4, description: 'Introduire une contrainte de régularité.' },
    ],
    observation: ['Regard trop proche', 'Trajectoire coupée', 'Perte de motricité'],
    risques: ['Chute par regard fixe', 'Trajectoire élargie'],
    influences: ['Vitesse', 'Adhérence', 'Type de moto'],
    erreurs: ['Rechercher la vitesse avant la trajectoire', 'Regarder chaque cône'],
    remediation: ['Ralentir le rythme', 'Reprendre par 2 boucles avant enchaînement'],
    questions: ["«Où étaient tes yeux quand tu as touché ce cône ?»"],
    critere: 'Le motard enchaîne le parcours sans faire tomber de cône, en conservant regard et fluidité.',
  },
  {
    id: 'retrogradage',
    title: 'Vérification du rétrogradage',
    objectif: "Le motard rétrograde de manière progressive et coordonnée sur une moto plus performante.",
    pourquoi: 'Sur moto puissante, un rétrogradage brutal peut bloquer la roue arrière ou déstabiliser la trajectoire.',
    comment: ['Anticiper le passage', 'Coordonner embrayage / accélération', 'Adapter le régime au ralentissement'],
    exercices: [
      { nom: 'Ligne droite descendante des rapports', niveau: 2, description: 'De 4e à 1re avec accompagnement moteur.' },
      { nom: 'Rétrogradage + virage', niveau: 4, description: 'Ralentir puis engager le virage.' },
    ],
    observation: ['À-coup moteur', 'Blocage roue arrière', 'Perte d\u2019équilibre'],
    risques: ['Blocage roue arrière', 'À-coup en virage', 'Perte d\u2019équilibre à basse vitesse'],
    influences: ['Puissance', 'Régime', 'Charge'],
    erreurs: ['Rétrograder sans ralentir', 'Relâcher l\u2019embrayage trop vite'],
    remediation: ['Reprise sur ligne droite plate', 'Décomposer accélération / rétrogradation'],
    questions: ["«Comment sentais-tu le régime moteur au moment de rétrograder ?»"],
    critere: 'Le motard rétrograde en descendant tous les rapports sans à-coup, avec accompagnement d\u2019embrayage progressif.',
  },
  {
    id: 'freinage-1-2',
    title: 'Freinage d\u2019urgence — 1 et 2 freins',
    objectif: "Le motard compare les sensations et les conséquences de chaque commande sur une moto plus performante.",
    pourquoi: 'Le freinage plus performant d\u2019une moto A demande une redécouverte pour ne pas bloquer une roue.',
    comment: ['Redresser la moto', 'Fermer les gaz', 'Freinage avant seul → sentir la puissance', 'Freinage 2 freins → sentir la stabilité ajoutée', 'Pression progressive'],
    exercices: [
      { nom: 'Freinage frein arrière seul', niveau: 2, description: 'Ressentir la limite.' },
      { nom: 'Freinage frein avant seul', niveau: 3, description: 'Ressentir la puissance.' },
      { nom: 'Freinage 2 freins', niveau: 4, description: 'Combiner pour distance d\u2019arrêt optimale.' },
    ],
    observation: ['Blocage roue', 'Freinage brutal', 'Regard sur l\u2019obstacle'],
    risques: ['Blocage roue avant (chute)', 'Blocage roue arrière (glissade)'],
    influences: ['Vitesse', 'Adhérence', 'Pneus', 'Charge', 'Temps de réaction'],
    erreurs: ['Serrer le frein avant d\u2019un coup', 'Sous-utiliser le frein avant par peur'],
    remediation: ['Reprise à allure plus faible', 'Isoler pression progressive'],
    questions: ["«Quel frein t\u2019a le plus surpris par rapport à ton A2 ?»"],
    critere: 'Le motard immobilise la moto dans la distance attendue en utilisant les 2 freins, sans blocage.',
    aRetenir: 'Je vois, je redresse, je freine, je contrôle.',
  },
  {
    id: 'contre-braquage',
    title: 'Contre-braquage — guidon, repose-pieds, jambes, bassin',
    objectif: "Le motard comprend le contre-braquage comme une coordination globale corps + moto, pas comme un simple geste guidon.",
    pourquoi: "La maîtrise de la trajectoire vient du corps entier — bras, jambes, bassin, regard — et pas seulement des mains.",
    comment: ['Action progressive sur le guidon', 'Appui stable sur les repose-pieds', 'Jambes en contact avec la moto', 'Bassin qui accompagne l\u2019inclinaison', 'Regard vers la sortie', 'Vitesse adaptée'],
    exercices: [
      { nom: 'Ressentir l\u2019appui repose-pieds', niveau: 2, description: 'Moto immobile, tester le transfert de poids sur les repose-pieds.' },
      { nom: 'Changement de direction G/D', niveau: 3, description: 'Sentir la coordination guidon + jambes + bassin.' },
      { nom: 'Slalom progressif', niveau: 4, description: 'Enchaîner les inclinaisons.' },
    ],
    observation: ['Crispation guidon', 'Trajectoire large', 'Regard fixe', 'Corps rigide'],
    risques: ['Perte de trajectoire', 'Chute par mouvement brutal'],
    influences: ['Vitesse', 'Pneus', 'Revêtement', 'Type de moto'],
    erreurs: ['«Il suffit de pousser le guidon»', 'Se crisper', 'Regarder les cônes'],
    remediation: ['Reprise à allure modérée', 'Focus jambes et repose-pieds isolés'],
    questions: ["«Que font tes jambes pendant un changement de direction ?»", "«Où est ton bassin ?»"],
    critere: 'Le motard enchaîne 4 changements de direction avec appui repose-pieds visible et regard vers la sortie.',
    aRetenir: 'La maîtrise de la trajectoire vient du corps entier, pas seulement des mains.',
  },
  {
    id: 'maniabilite-30',
    title: 'Maniabilité à 30 km/h — épingles à cheveux',
    objectif: "Le motard aborde une vitesse intermédiaire entre allure lente et conduite dynamique.",
    pourquoi: 'Vitesse charnière : parfaite pour travailler regard, position et contre-braquage combinés.',
    comment: ['Regard → Position → Vitesse → Trajectoire → Contre-braquage → Sortie'],
    exercices: [
      { nom: 'Épingles larges', niveau: 3, description: 'Apprendre la coordination.' },
      { nom: 'Épingles serrées', niveau: 5, description: 'Réduction du rayon disponible.' },
    ],
    observation: ['Vitesse d\u2019entrée trop élevée', 'Trajectoire large', 'Freinage pendant la manœuvre'],
    risques: ['Sortie large', 'Chute intérieure sur cône serré'],
    influences: ['Puissance moto', 'Adhérence', 'Vent'],
    erreurs: ['Freiner en pleine épingle', 'Regarder l\u2019extérieur du virage'],
    remediation: ['Reprise plus large', 'Décomposition en 3 séquences'],
    questions: ["«À quel moment as-tu décidé de la vitesse d\u2019entrée ?»"],
    critere: 'Le motard négocie l\u2019épingle sans freiner en virage, avec sortie dans l\u2019axe attendu.',
  },
  {
    id: 'evitement',
    title: 'Évitement',
    objectif: "Le motard identifie qu'une situation dangereuse peut nécessiter d'éviter plutôt que de freiner.",
    pourquoi: 'Freiner n\u2019est pas toujours la bonne réponse : l\u2019évitement introduit une autre solution face à un danger.',
    comment: ['Identifier l\u2019obstacle', 'Adapter la vitesse en amont', 'Regard vers la zone de sortie', 'Déclencher le contre-braquage', 'Revenir progressivement dans l\u2019axe', 'Freiner seulement après si la situation le permet'],
    exercices: [
      { nom: 'Évitement à allure modérée', niveau: 3, description: 'Ressentir la logique regard + contre-braquage.' },
      { nom: 'Évitement G puis D', niveau: 4, description: 'Alterner les deux sens.' },
    ],
    observation: ['Fixation de l\u2019obstacle', 'Freinage pendant l\u2019évitement', 'Mouvement brutal du guidon'],
    risques: ['Perte de trajectoire', 'Chute par mouvement brutal'],
    influences: ['Vitesse', 'Distance disponible', 'Regard', 'Adhérence'],
    erreurs: ['Vouloir freiner et éviter en même temps', 'Fixer l\u2019obstacle des yeux'],
    remediation: ['Reprise à allure faible sans freinage associé', 'Décomposer regard → contre-braquage → retour axe'],
    questions: ["«Si tu regardes l\u2019obstacle, où risques-tu d\u2019aller ?»"],
    critere: 'Le motard évite l\u2019obstacle sans le toucher, avec trajectoire de sortie contrôlée.',
    aRetenir: 'Je regarde où je veux aller, pas l\u2019obstacle que je veux éviter.',
  },
];

/* ================================================================== */
/*  PHASE 3 — CIRCULATION (~3 h)                                       */
/* ================================================================== */
const P3_CIRCULATION = [
  {
    id: 'vehicule',
    title: 'Prise en main de la moto catégorie A',
    objectif: "Le motard adapte ses repères de conduite à une moto plus performante en conditions réelles.",
    pourquoi: 'Les 15 premières minutes sont critiques : elles conditionnent tout le reste de la séance.',
    comment: ['Vérifications avant départ', 'Prise en main statique', 'Prise en main dynamique progressive', 'Ajuster ses repères de conduite'],
    exercices: [
      { nom: 'Insertion progressive à 50 km/h', niveau: 3, description: 'Ressentir la moto avant de monter en vitesse.' },
      { nom: 'Trajet urbain calme', niveau: 4, description: 'Adapter les repères de freinage et d\u2019accélération.' },
    ],
    observation: ['Sur-estimation de sa marge', 'Sous-estimation du freinage', 'Sur-régime involontaire'],
    risques: ['Freinage insuffisant sur pédale nouvelle', 'Ouverture gaz trop vive'],
    influences: ['Cylindrée', 'Rapport poids / puissance', 'Type de moto'],
    erreurs: ['Vouloir tester la puissance immédiatement', 'Rouler comme en A2'],
    remediation: ['Reprise à allure encore plus basse', 'Verbalisation de chaque adaptation'],
    questions: ["«Qu\u2019as-tu senti dans les 5 premières minutes ?»"],
    critere: 'Le motard adapte ses repères de freinage et d\u2019accélération en moins de 10 min, sans à-coup notable.',
  },
  {
    id: 'roulage-tour-role',
    title: 'Roulage par tour de rôle — 1 h par motard',
    objectif: "Chaque motard conduit dans des conditions variées et se confronte à des situations concrètes.",
    pourquoi: 'La confrontation à la diversité des situations est le meilleur révélateur des acquis et des fragilités.',
    comment: ['Insertion', 'Changements de direction', 'Virages', 'Freinages', 'Gestion des intersections', 'Anticipation'],
    exercices: [
      { nom: 'Trajet urbain 15 min', niveau: 3, description: 'Focus insertion + changements de direction.' },
      { nom: 'Trajet route 30 min', niveau: 4, description: 'Focus virages + dépassements + distances.' },
      { nom: 'Trajet varié 15 min', niveau: 5, description: 'Synthèse.' },
    ],
    observation: ['Fatigue', 'Baisse de vigilance', 'Excès de confiance'],
    risques: ['Erreur d\u2019anticipation', 'Freinage tardif', 'Sortie de route'],
    influences: ['Trafic', 'Météo', 'Type de route'],
    erreurs: ['Perdre de la vigilance en fin de tour', 'Rouler «à la performance» pour impressionner'],
    remediation: ['Pause à mi-parcours', 'Reprise sur type de route problématique'],
    questions: ["«À quel moment as-tu senti la fatigue ?»"],
    critere: 'Le motard conduit 1 h sans erreur majeure, avec 2 points forts et 2 axes de progrès identifiés.',
  },
  {
    id: 'observation',
    title: 'Observation par les autres stagiaires',
    objectif: "Chaque motard développe son sens de l'observation en analysant la conduite des autres.",
    pourquoi: 'Observer les autres, c\u2019est aussi progresser soi-même : mise à distance et transfert d\u2019apprentissage.',
    comment: ['Observer la position sur la chaussée', 'Analyser les prises d\u2019information', 'Évaluer l\u2019anticipation et les trajectoires', 'Repérer les points forts ET les axes de progrès', 'Formuler une critique constructive'],
    exercices: [
      { nom: 'Grille d\u2019observation simple', niveau: 2, description: '4 points à observer par tour.' },
      { nom: 'Restitution orale', niveau: 3, description: 'Formuler observations + hypothèses + questions.' },
    ],
    observation: ['Jugement sans explication', 'Focus uniquement sur les erreurs'],
    risques: ['Critique non constructive', 'Effet démobilisant'],
    influences: ['Attention', 'Position d\u2019observation', 'Communication'],
    erreurs: ['«Il conduit mal»', 'Focus uniquement sur les erreurs'],
    remediation: ['Reformulation guidée', 'Grille structurée avec critères'],
    questions: ["«Qu\u2019a-t-il bien fait à ton avis ?»", "«Que ferais-tu différemment à sa place ?»"],
    critere: 'Le motard formule une observation avec au moins 1 point fort, 1 axe de progrès et 1 question.',
    aRetenir: 'Observer les autres, c\u2019est aussi progresser soi-même.',
  },
  {
    id: 'debriefing',
    title: 'Débriefing collectif & bilan du formateur',
    objectif: "Le motard confronte son ressenti aux observations du groupe et intègre le bilan du formateur.",
    pourquoi: 'Structurer le débriefing : auto-évaluation d\u2019abord, puis observations, puis bilan du formateur, puis plan d\u2019action.',
    comment: ['Auto-évaluation du conducteur d\u2019abord', 'Observations des autres motards', 'Bilan du formateur (points forts + axes de progrès)', 'Définition des points à retravailler'],
    exercices: [
      { nom: 'Auto-évaluation en 3 phrases', niveau: 2, description: 'Ce qui a bien marché, ce qui n\u2019a pas marché, pourquoi.' },
      { nom: 'Restitution structurée du formateur', niveau: 3, description: 'Points forts / axes / plan d\u2019action.' },
    ],
    observation: ['Refus de l\u2019analyse', 'Position défensive', 'Auto-évaluation excessive'],
    risques: ['Discussion stérile', 'Rejet du groupe'],
    influences: ['Climat du groupe', 'Qualité des observations', 'Écoute'],
    erreurs: ['Rester défensif', 'Discuter l\u2019évidence'],
    remediation: ['Reformulation neutre', 'Reprise sur un point précis'],
    questions: ["«Quel est LE point que tu retiens ?»", "«Que vas-tu changer sur le prochain tour ?»"],
    critere: 'Le motard formule un plan d\u2019action concret avec 1 objectif clair pour le tour suivant.',
  },
  {
    id: 'correction',
    title: 'Correction des points faibles en circulation',
    objectif: "Le motard reprend concrètement les difficultés observées, pas seulement en parler.",
    pourquoi: 'Le débriefing sans reprise reste théorique. La correction en situation ancre l\u2019apprentissage.',
    comment: ['Reprise du point identifié', 'Mise en situation ciblée', 'Nouvel essai', 'Nouvelle évaluation'],
    exercices: [
      { nom: 'Reprise ciblée sur un point', niveau: 3, description: 'Trajet court avec focus unique.' },
      { nom: 'Vérification du transfert', niveau: 4, description: 'Trajet varié pour confirmer.' },
    ],
    observation: ['Passer à autre chose sans avoir corrigé', 'Corriger sans expliquer le pourquoi'],
    risques: ['Répétition d\u2019erreur en situation réelle plus tard'],
    influences: ['Temps disponible', 'Météo', 'Type de route'],
    erreurs: ['Faire semblant que ça marche', 'Enchaîner sans consolider'],
    remediation: ['Ralentir', 'Décomposer en micro-étapes'],
    questions: ["«Qu\u2019est-ce qui a changé cette fois-ci ?»"],
    critere: 'Le motard reproduit correctement la compétence sur 3 essais consécutifs après la correction.',
  },
  {
    id: 'autonomie',
    title: 'Conduite autonome — ≈ 30 minutes',
    objectif: "Le motard gère seul son environnement, ses trajectoires, ses décisions.",
    pourquoi: 'La conduite autonome n\u2019est pas la fin de la formation, c\u2019est le début de la conduite responsable.',
    comment: ['Environnement', 'Trajectoires', 'Changements de direction', 'Adaptation d\u2019allure', 'Prises d\u2019information', 'Décisions'],
    exercices: [
      { nom: 'Trajet autonome libre', niveau: 5, description: 'Le formateur suit sans intervenir sauf urgence.' },
      { nom: 'Trajet autonome avec 1 objectif choisi par l\u2019élève', niveau: 6, description: "Ex : «Je veux améliorer mes prises d'information»." },
    ],
    observation: ['Baisse de vigilance', 'Erreur d\u2019anticipation', 'Sur-confiance'],
    risques: ['Erreur de trajectoire sans correction externe', 'Sortie de route'],
    influences: ['Fatigue', 'Type de route', 'Trafic'],
    erreurs: ['Rechercher la performance au lieu de la sécurité', 'Sous-estimer la fatigue'],
    remediation: ['Reprise avec accompagnement', 'Analyse du moment de perte d\u2019attention'],
    questions: ["«À quel moment as-tu senti que tu conduisais mieux qu\u2019accompagné ?»"],
    critere: 'Le motard conduit 30 min en autonomie sans erreur majeure, avec analyse lucide de sa prestation.',
    aRetenir: 'La conduite autonome n\u2019est pas la fin de la formation : c\u2019est le début de la conduite responsable.',
  },
];

export const PHASES_PASSERELLE = [
  { id: 'theorie',         code: '1', title: 'Théorie',                    subtitle: "Analyser son expérience A2, projeter sa conduite en catégorie A", duration: '≈ 2 heures',  color: '#0d9488', context: 'salle',       accroche: "La partie théorique s'appuie sur l'expérience acquise pendant la période A2. Le formateur part du vécu du stagiaire.", exercises: P1_THEORIE },
  { id: 'hors-circulation',code: '2', title: 'Formation hors circulation', subtitle: 'Adapter sa technique à une moto plus performante',                   duration: '≈ 2 heures',  color: '#16a34a', context: 'plateau',     accroche: "Les exercices sont sélectionnés par le formateur en fonction du niveau, de l'expérience et des objectifs exprimés par l'élève. Ce n'est pas un catalogue obligatoire.", exercises: P2_HORS_CIRC, disclaimer: "Les exercices sont adaptés par l'enseignant en fonction des besoins et des objectifs exprimés par chaque élève." },
  { id: 'circulation',     code: '3', title: 'Formation en circulation',   subtitle: 'Observer, analyser, conduire en autonomie',                          duration: '≈ 3 heures',  color: '#ea580c', context: 'circulation', accroche: "Chaque motard conduit environ une heure. Pendant qu'un stagiaire conduit, les autres deviennent observateurs et évaluent la prestation. Débriefing collectif + correction ciblée + changement de conducteur.", exercises: P3_CIRCULATION },
];

export function buildSynthesePasserelle() {
  const lines = [];
  lines.push('# Passerelle A2 → A (7 h) — Progression pédagogique CODE 2 ROUES');
  lines.push('');
  lines.push(`**Méthode :** ${INTRO_PASSERELLE.baseline}`);
  lines.push('');
  lines.push(`> ${INTRO_PASSERELLE.principe}`);
  lines.push('');
  PHASES_PASSERELLE.forEach(phase => {
    lines.push(`## ${phase.code}. ${phase.title.toUpperCase()} — ${phase.duration}`);
    lines.push(`*${phase.subtitle}*`);
    lines.push('');
    lines.push(phase.accroche);
    lines.push('');
    phase.exercises.forEach(ex => {
      lines.push(`### ${ex.title}`);
      lines.push(`**🎯 Objectif :** ${ex.objectif}`);
      lines.push(`**Pourquoi ?** ${ex.pourquoi}`);
      lines.push('**Comment ?**');
      ex.comment.forEach(c => lines.push(`- ${c}`));
      lines.push('**🏍️ Exercices :**');
      ex.exercices.forEach(e => lines.push(`- **${e.nom}** _(niv. ${e.niveau})_ : ${e.description}`));
      lines.push('**👀 Observation :** ' + ex.observation.join(' · '));
      lines.push('**⚠️ Risques :** ' + ex.risques.join(' · '));
      lines.push('**🌦️ Influences :** ' + ex.influences.join(' · '));
      lines.push('**Erreurs fréquentes :** ' + ex.erreurs.join(' · '));
      lines.push('**🔧 Remédiation :** ' + ex.remediation.join(' · '));
      lines.push('**🗣️ Questions :**');
      ex.questions.forEach(q => lines.push(`- ${q}`));
      lines.push(`**✅ Critère de réussite :** ${ex.critere}`);
      if (ex.aRetenir) lines.push(`> **À retenir :** ${ex.aRetenir}`);
      lines.push('');
    });
    if (phase.disclaimer) lines.push(`*${phase.disclaimer}*\n`);
  });
  return lines.join('\n');
}
